'use client'

import { memo, useMemo, useState } from 'react'
import { Card } from '@heroui/react'
import { EmptyState, LineChart, NumberValue, Segment, TrendChip } from '@heroui-pro/react'
import { CalendarClock } from 'lucide-react'
import { isDealInPipelineWindow, getClosingPipeline, formatPipelineAxis, getPipelineTrend, type PipelineWindowKey } from '../../lib/pipeline'
import type { Deal } from '../../lib/types'
import { formatMoney } from '../../lib/view'

const pipelineRangeOptions: { id: PipelineWindowKey; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'next-90', label: '90D' },
]

export const ClosingPipelineCard = memo(function ClosingPipelineCard({ deals, isLoading }: { deals: Deal[]; isLoading: boolean }): React.ReactElement {
  const [range, setRange] = useState<PipelineWindowKey>('all')
  const pipelineDeals = useMemo(() => deals.filter((deal) => isDealInPipelineWindow(deal, range)), [deals, range])
  const pipeline = useMemo(() => getClosingPipeline(pipelineDeals), [pipelineDeals])
  const total = pipeline.reduce((sum, point) => sum + point.value, 0)
  const trend = getPipelineTrend(pipeline)

  return (
    <Card>
      <Card.Header className="flex-col items-start gap-4 pb-0 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-3">
          <Card.Title className="text-base font-semibold text-foreground">Closing Pipeline</Card.Title>
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted">Scheduled close value</span>
            <NumberValue
              className="text-2xl font-semibold tabular-nums text-foreground"
              currency="USD"
              maximumFractionDigits={1}
              notation="compact"
              style="currency"
              value={total}
            />
            <div className="flex items-center gap-2 text-xs">
              <TrendChip className="bg-transparent px-0" trend={trend.trend}>
                {trend.label}
              </TrendChip>
              <span className="text-muted">{pipeline.length} month{pipeline.length === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>
        <Segment
          aria-label="Pipeline range"
          selectedKey={range}
          size="sm"
          variant="ghost"
          onSelectionChange={(key) => {
            const nextKey = String(key) as PipelineWindowKey
            if (pipelineRangeOptions.some((option) => option.id === nextKey)) {
              setRange(nextKey)
            }
          }}
        >
          {pipelineRangeOptions.map((option) => (
            <Segment.Item key={option.id} id={option.id}>
              {option.label}
            </Segment.Item>
          ))}
        </Segment>
      </Card.Header>
      <Card.Content>
        {isLoading || pipeline.length === 0 ? (
          <EmptyState className="min-h-[220px] bg-surface-secondary" size="sm">
            <EmptyState.Header>
              <EmptyState.Media variant="icon">
                <CalendarClock className="size-5" />
              </EmptyState.Media>
              <EmptyState.Title>No Close Dates</EmptyState.Title>
              <EmptyState.Description>No scheduled closes in this range.</EmptyState.Description>
            </EmptyState.Header>
          </EmptyState>
        ) : (
          <LineChart data={pipeline} height={240} margin={{ bottom: 0, left: 0, right: 8, top: 8 }}>
            <LineChart.Grid vertical={false} />
            <LineChart.XAxis dataKey="month" tickMargin={8} />
            <LineChart.YAxis tickFormatter={formatPipelineAxis} width={52} />
            <LineChart.Line
              dataKey="value"
              dot={false}
              name="Scheduled value"
              stroke="var(--chart-3)"
              strokeWidth={2}
              type="monotone"
            />
            <LineChart.Tooltip
              content={<LineChart.TooltipContent valueFormatter={(value) => formatMoney(Number(value))} />}
            />
          </LineChart>
        )}
      </Card.Content>
    </Card>
  )
})
